import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiFehler, lies, schick } from "../api/client";
import type { AuthConfig, AuthStart, Ich } from "../api/types";
import { BEHAELTER_ID, ladeTurnstile } from "../lib/turnstile";

/** Zwei Schritte wie im Haus: Adresse → Code. Externe kommen nur mit Einladung (E6). */
export function Anmelden() {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [fehler, setFehler] = useState("");
  const [laeuft, setLaeuft] = useState(false);

  // Turnstile wie in lilasprojekte (lib/turnstile.ts): Sitekey zur Laufzeit aus
  // /auth/config, explizites render(), Wertmarke nach jedem Absenden verbraucht.
  const [sitekey, setSitekey] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const widgetRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let lebt = true;
    lies<AuthConfig>("/auth/config")
      .then((c) => { if (lebt) setSitekey(c.turnstile_sitekey); })
      .catch(() => { if (lebt) setSitekey(null); });
    return () => { lebt = false; };
  }, []);

  // Das Widget steht nur im ersten Schritt; „Andere Adresse" zeichnet ein frisches.
  useEffect(() => {
    if (!sitekey || challenge) return;
    let lebt = true;
    let id: string | undefined;
    ladeTurnstile()
      .then((api) => {
        if (!lebt || !document.getElementById(BEHAELTER_ID)) return;
        id = api.render(`#${BEHAELTER_ID}`, {
          sitekey,
          action: "login",
          theme: "light",
          callback: (tk) => setToken(tk),
          "error-callback": () => { setToken(null); setFehler("Die Sicherheitsprüfung ist fehlgeschlagen. Bitte neu laden."); },
          "expired-callback": () => setToken(null),
        });
        widgetRef.current = id;
      })
      .catch((e: unknown) => { if (lebt) setFehler(e instanceof Error ? e.message : "Die Sicherheitsprüfung lädt nicht."); });
    return () => {
      lebt = false;
      if (id && window.turnstile) {
        try { window.turnstile.remove(id); } catch { /* schon weg */ }
      }
      widgetRef.current = undefined;
      setToken(null);
    };
  }, [sitekey, challenge]);

  const pruefungZurueck = useCallback(() => {
    setToken(null);
    if (widgetRef.current && window.turnstile) {
      try { window.turnstile.reset(widgetRef.current); } catch { /* Widget ist weg */ }
    }
  }, []);

  async function weiter(e: FormEvent) {
    e.preventDefault();
    setFehler("");
    setLaeuft(true);
    try {
      if (!challenge) {
        try {
          const r = await schick<AuthStart>("/auth/start", { email, turnstile: token });
          setChallenge(r.challenge);
        } finally {
          pruefungZurueck();
        }
      } else {
        await schick("/auth/verify", { challenge, code, angemeldet_bleiben: true });
        qc.setQueryData(["ich"], await lies<Ich>("/me"));
      }
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : "Das hat nicht geklappt.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <main className="anmeldung">
      <form className="karte" onSubmit={weiter}>
        <img src="/logo.svg" alt="LILASp" />
        <h1>Bauleitung</h1>
        <p className="dim" style={{ margin: 0 }}>
          {challenge
            ? `Der Code ist an ${email} unterwegs. Er gilt zehn Minuten.`
            : "Anmeldung mit der E-Mail-Adresse. Es gibt kein Passwort – ein Code kommt per Mail."}
        </p>
        {!challenge ? (
          <label className="feld">
            <span>E-Mail</span>
            <input className="eingabe" type="email" autoComplete="email" required autoFocus
                   value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
        ) : (
          <label className="feld">
            <span>Code</span>
            <input className="eingabe ziffern" inputMode="numeric" autoComplete="one-time-code"
                   required autoFocus maxLength={12} value={code}
                   onChange={(e) => setCode(e.target.value)} />
          </label>
        )}
        {/* Behälter heißt `pruefung`, NIE `turnstile` (lib/turnstile.ts). */}
        {!challenge && <div id={BEHAELTER_ID} style={{ minHeight: sitekey ? 68 : 0 }} />}
        {fehler && <div className="meldung">{fehler}</div>}
        <button className="knopf haupt" disabled={laeuft || (!challenge && !!sitekey && !token)}>
          {challenge ? "Anmelden" : "Code anfordern"}
        </button>
        {challenge && (
          <button type="button" className="knopf leise"
                  onClick={() => { setChallenge(null); setCode(""); setFehler(""); }}>
            Andere Adresse
          </button>
        )}
      </form>
    </main>
  );
}
